import React, { useMemo } from 'react';

import type { Question, QuestionOption } from '../../types';
import { QuestionStatusLine } from './QuestionStatusLine';

type Props = {
  question: Question;
  /** The ranking so far, best first. Items not yet ranked are left out. */
  selectedOptions: number[];
  onChange: (orderedOptionIds: number[]) => void;
  disabled?: boolean;
};

const optionLabel = (option: QuestionOption | undefined) =>
  (option?.front_text || option?.back_text || '').trim();

/**
 * Ranking by tapping: the learner taps items in order of preference. Each tapped item moves into a
 * numbered list and can be taken back out. Nothing is ranked until the learner ranks it.
 */
export const DragAndDropRanking: React.FC<Props> = ({
  question,
  selectedOptions,
  onChange,
  disabled = false,
}) => {
  const options = useMemo(
    () => [...(question.options ?? [])].sort((a, b) => {
      const left = typeof a.order === 'number' ? a.order : 0;
      const right = typeof b.order === 'number' ? b.order : 0;
      return left - right;
    }),
    [question.options],
  );
  const optionsById = useMemo(() => new Map(options.map((option) => [option.id, option])), [options]);
  const ranked = selectedOptions.filter((id) => optionsById.has(id));
  const remaining = options.filter((option) => !ranked.includes(option.id));
  const isComplete = options.length > 0 && remaining.length === 0;

  const rank = (optionId: number) => {
    if (disabled || ranked.includes(optionId)) return;
    onChange([...ranked, optionId]);
  };

  const unrank = (optionId: number) => {
    if (disabled) return;
    onChange(ranked.filter((id) => id !== optionId));
  };

  return (
    <div className="w-full" data-testid="ranking-question">
      {ranked.length > 0 ? (
        <ol aria-label="Your ranking" className="mb-4 flex flex-col gap-2">
          {ranked.map((optionId, index) => {
            const label = optionLabel(optionsById.get(optionId));
            return (
              <li
                key={optionId}
                data-testid={`ranking-ranked-${optionId}`}
                className="flex items-center gap-3 rounded-xl bg-white px-3 py-2.5 shadow-elevation-sm"
              >
                <span
                  aria-hidden="true"
                  className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-foundation-base-darkbase-dark-500 text-xs font-semibold text-white"
                >
                  {index + 1}
                </span>
                <span className="flex-1 type-body-m text-foundation-base-darkbase-dark-500">{label}</span>
                <button
                  type="button"
                  disabled={disabled}
                  aria-label={`Take ${label} out of your ranking`}
                  onClick={() => unrank(optionId)}
                  className="min-h-11 rounded-md px-2 text-sm font-semibold text-foundation-base-darkbase-dark-300 hover:text-foundation-base-darkbase-dark-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foundation-orange-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Undo
                </button>
              </li>
            );
          })}
        </ol>
      ) : null}

      {remaining.length > 0 ? (
        <div role="group" aria-label={ranked.length ? `Choose your number ${ranked.length + 1}` : 'Choose your number 1'}>
          <p aria-hidden="true" className="mb-2 text-sm font-semibold text-foundation-base-darkbase-dark-500">
            {ranked.length ? `Tap your number ${ranked.length + 1}` : 'Tap your number 1'}
          </p>
          <div className="flex flex-wrap gap-2">
            {remaining.map((option) => (
              <button
                key={option.id}
                type="button"
                disabled={disabled}
                data-testid={`ranking-option-${option.id}`}
                onClick={() => rank(option.id)}
                className="min-h-11 rounded-full border border-dashed border-foundation-base-darkbase-dark-200 bg-white/80 px-4 py-2 text-left text-[0.9375rem] leading-5 text-foundation-base-darkbase-dark-500 transition-colors hover:border-foundation-base-darkbase-dark-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foundation-orange-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {optionLabel(option)}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <QuestionStatusLine ready={isComplete}>
        {isComplete
          ? 'All ranked. Tap Undo on any item to change it.'
          : `${ranked.length} of ${options.length} ranked.`}
      </QuestionStatusLine>
    </div>
  );
};
